import React, { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { Label } from "@finance/components/ui/label";
import { Textarea } from "@finance/components/ui/textarea";
import { Switch } from "@finance/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@finance/components/ui/dialog";
import { toast } from "sonner";
import { Plus } from "@phosphor-icons/react";

const DOC_TYPES = [
  ["invoice", "Tax Invoice"],
  ["delivery_note", "Delivery Note"],
  ["purchase_order", "Purchase Order"],
  ["grv", "Goods Received Voucher"],
  ["goods_return", "Goods Returned Note"],
  ["credit_note", "Credit Note"],
  ["debit_note", "Debit Note"],
  ["payment_voucher", "Payment Voucher"],
  ["transaction_card", "Transaction Card"],
];

export default function Disclaimers() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ active: true });

  const load = useCallback(() => api.get("/disclaimers").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.title || !form.body) { toast.error("Title and body are required"); return; }
    try {
      if (form.id) await api.put(`/disclaimers/${form.id}`, form);
      else await api.post("/disclaimers", form);
      toast.success("Disclaimer saved"); setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (d) => {
    if (!window.confirm(`Delete disclaimer "${d.title}"?`)) return;
    await api.delete(`/disclaimers/${d.id}`); toast.success("Deleted"); load();
  };
  const toggleType = (t) => setForm((f) => {
    const cur = f.doc_types || [];
    return { ...f, doc_types: cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t] };
  });
  const typeLabel = (t) => (DOC_TYPES.find((d) => d[0] === t) || [t, t])[1];

  return (
    <Layout>
      <PageHeader
        title="Disclaimers"
        subtitle="Reusable terms you can attach when printing invoices, delivery notes and purchase orders"
        actions={<Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="new-disclaimer-btn" onClick={() => { setForm({ active: true }); setOpen(true); }}><Plus size={16} className="mr-1" />New Disclaimer</Button>}
      />
      <div className="p-8">
        <div className="space-y-3">
          {rows.length === 0 && <div className="rounded-sm border border-dashed border-gray-200 p-10 text-center text-sm text-gray-400">No disclaimers yet. Create one to include on printed documents.</div>}
          {rows.map((d) => (
            <div key={d.id} data-testid={`disclaimer-row-${d.id}`} className="flex items-start justify-between rounded-sm border border-gray-200 bg-white p-4">
              <div>
                <div className="flex items-center gap-2 font-medium">{d.title}{!d.active && <span className="rounded-sm bg-gray-100 px-2 py-0.5 text-xs text-gray-500">inactive</span>}</div>
                <div className="mt-1 max-w-3xl text-sm text-gray-600">{d.body}</div>
                {(d.doc_types || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`disc-types-${d.id}`}>
                    {(d.doc_types || []).map((t) => (
                      <span key={t} className="rounded-sm bg-[#E6ECF5] px-1.5 py-0.5 text-[10px] font-semibold text-[#122E5C]">{typeLabel(t)}</span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex shrink-0 gap-3">
                <button className="text-xs text-gray-500 hover:text-[#1B4383]" data-testid={`edit-disclaimer-${d.id}`} onClick={() => { setForm(d); setOpen(true); }}>Edit</button>
                <button className="text-xs text-gray-400 hover:text-red-500" data-testid={`delete-disclaimer-${d.id}`} onClick={() => del(d)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="disclaimer-dialog">
          <DialogHeader><DialogTitle className="font-display">{form.id ? "Edit" : "New"} Disclaimer</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="lx-label">Title</Label><Input className="mt-1" data-testid="disclaimer-title" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div><Label className="lx-label">Body</Label><Textarea className="mt-1" rows={4} data-testid="disclaimer-body" value={form.body || ""} onChange={(e) => setForm({ ...form, body: e.target.value })} /></div>
            <div>
              <Label className="lx-label">Applies to documents</Label>
              <p className="mb-2 text-xs text-gray-400">Ticked documents pre-suggest this disclaimer when printing.</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                {DOC_TYPES.map(([t, label]) => (
                  <label key={t} className="flex items-center gap-2 text-sm" data-testid={`disclaimer-type-${t}`}>
                    <input type="checkbox" checked={(form.doc_types || []).includes(t)} onChange={() => toggleType(t)} />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm"><Switch checked={!!form.active} data-testid="disclaimer-active" onCheckedChange={(v) => setForm({ ...form, active: v })} /> Active</label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="save-disclaimer" onClick={save}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
